import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { User, CreditCard, Wallet, Tag, Calendar, CheckCircle, Trash2, Plus, Star, Loader2, Phone, MapPin, FileText, Eye, Download, Clock, XCircle, RefreshCw, AlertCircle, UserPlus, ExternalLink, QrCode } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatCPF, formatPhone, formatCEP, fetchAddressByCEP, validateCPF } from "@/lib/validations";
import { CardData, validateCardNumber, formatCardNumber, getCardBrand } from "@/lib/pagarme";
import { SubscriptionModal } from "@/components/payment/SubscriptionModal";
import { TagManager } from "@/components/tags/TagManager";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRAZILIAN_STATES } from "@/lib/constants/brazilian-states";
import { ConnectedUsersManager } from "@/components/connected-users/ConnectedUsersManager";
import { fetchPlansForUser } from "@/lib/subscription-plans";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

interface Profile {
  id?: string;
  full_name?: string | null;
  cpf?: string | null;
  health_area?: string | null;
  health_area_id?: string | null;
  avatar_url?: string | null;
  phone?: string | null;
  address_zipcode?: string | null;
  address_street?: string | null;
  address_number?: string | null;
  address_complement?: string | null;
  address_neighborhood?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  tiny_customer_id?: string | null;
}

interface Subscription {
  id: string;
  plan_code: string;
  status: string;
  price_cents: number;
  next_billing_date: string | null;
  started_at: string;
  canceled_at: string | null;
  payment_method: string | null;
  card_id: string | null;
}

interface SubscriptionPlan {
  code: string;
  name: string;
  description: string;
  min_patients: number;
  max_patients: number | null;
  price_cents: number;
  features: string[];
}

interface PaymentCard {
  id: string;
  pagarme_card_id: string;
  last_four_digits: string;
  brand: string;
  holder_name: string;
  exp_month: number;
  exp_year: number;
  is_default: boolean;
}

interface Invoice {
  id: string;
  amount_cents: number;
  status: string;
  paid_at: string | null;
  created_at: string;
  pagarme_charge_id: string | null;
  payment_method: string | null;
  nfse_url: string | null;
  nfse_pdf_url: string | null;
  tiny_nfse_id: string | null;
  nfse_number: string | null;
  pix_qr_code_url?: string | null;
  pix_expires_at?: string | null;
}

export default function MinhaConta() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState(tab || "perfil");

  // Profile states
  const [profile, setProfile] = useState<Profile>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Subscription states
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [switchingPaymentMethod, setSwitchingPaymentMethod] = useState(false);
  const [showSwitchToCardDialog, setShowSwitchToCardDialog] = useState(false);
  const [selectedCardForSwitch, setSelectedCardForSwitch] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState<string | null>(null);

  // Cards states
  const [loadingCards, setLoadingCards] = useState(true);
  const [cards, setCards] = useState<PaymentCard[]>([]);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showDeleteCardDialog, setShowDeleteCardDialog] = useState(false);
  const [selectedCard, setSelectedCard] = useState<PaymentCard | null>(null);
  const [processing, setProcessing] = useState(false);
  const [cardData, setCardData] = useState<CardData>({
    number: "",
    holder_name: "",
    exp_month: "",
    exp_year: "",
    cvv: "",
  });

  // Invoices states
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [generatingNfse, setGeneratingNfse] = useState<string | null>(null);
  const [syncingTinyCustomer, setSyncingTinyCustomer] = useState(false);
  const [tinyCustomerSynced, setTinyCustomerSynced] = useState(false);
  const [loadingPix, setLoadingPix] = useState<string | null>(null);
  const [pixDialogData, setPixDialogData] = useState<{
    pix_qr_code: string;
    pix_qr_code_url: string;
    pix_expires_at: string | null;
    amount_cents: number;
    regenerated?: boolean;
  } | null>(null);
  const [upgradePixData, setUpgradePixData] = useState<{
    pix_qr_code: string;
    pix_qr_code_url: string | null;
    amount_cents: number;
    plan_name: string;
  } | null>(null);

  // Health Areas state
  const [healthAreas, setHealthAreas] = useState<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    loadAllData();
    loadHealthAreas();
  }, []);

  useEffect(() => {
    setCurrentTab(tab || "perfil");
  }, [tab]);

  const handleTabChange = (value: string) => {
    setCurrentTab(value);
    navigate(`/app/minha-conta/${value}`);
  };

  async function loadHealthAreas() {
    try {
      const { data, error } = await supabase
        .from("health_areas")
        .select("id, name")
        .eq("is_active", true)
        .order("display_order");

      if (error) throw error;
      setHealthAreas(data || []);
    } catch (error: any) {
      console.error("Error loading health areas:", error);
    }
  }

  async function loadAllData() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      setProfile(profileData || {});
      setAvatarUrl(profileData?.avatar_url || null);

      // Load subscription data
      await loadSubscriptionData(user.id);

      // Load cards
      await loadCards();

      // Load invoices
      await loadInvoices();
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }

  // ========== PROFILE FUNCTIONS ==========

  async function handleSaveProfile() {
    try {
      setSaving(true);

      if (profile.cpf && !validateCPF(profile.cpf)) {
        toast.error("CPF inválido");
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Sessão expirada");

      const { error } = await supabase
        .from("profiles")
        .update(profile)
        .eq("id", user.id);

      if (error) throw error;

      toast.success("Perfil atualizado com sucesso");
    } catch (error: any) {
      console.error("Error saving profile:", error);
      toast.error(error.message || "Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  }

  async function handleCEPChange(cep: string) {
    const formatted = formatCEP(cep);
    setProfile(p => ({ ...p, address_zipcode: formatted }));

    const cleanCEP = cep.replace(/\D/g, '');
    if (cleanCEP.length === 8) {
      try {
        const address = await fetchAddressByCEP(cep);
        setProfile(p => ({
          ...p,
          address_street: address.street,
          address_neighborhood: address.neighborhood,
          address_city: address.city,
          address_state: address.state,
        }));
        toast.success("Endereço encontrado!");
      } catch (error: any) {
        toast.error(error.message || "Erro ao buscar CEP");
      }
    }
  }

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setUploading(true);

      if (!event.target.files || event.target.files.length === 0) return;

      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fileName = `${user.id}/avatar.${fileExt}`;

      if (avatarUrl) {
        const oldPath = avatarUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('avatars').remove([oldPath]);
      }

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: data.publicUrl })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(data.publicUrl);
      toast.success('Foto atualizada');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Erro ao fazer upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      setUploading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      if (avatarUrl) {
        const oldPath = avatarUrl.split('/').slice(-2).join('/');
        await supabase.storage.from('avatars').remove([oldPath]);
      }

      const { error } = await supabase
        .from('profiles')
        .update({ avatar_url: null })
        .eq('id', user.id);

      if (error) throw error;

      setAvatarUrl(null);
      toast.success('Foto removida');
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast.error('Erro ao remover foto');
    } finally {
      setUploading(false);
    }
  };

  // ========== SUBSCRIPTION FUNCTIONS ==========

  async function loadSubscriptionData(userId: string) {
    try {
      const { data: activeSub } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "active")
        .maybeSingle();

      let subData = activeSub;
      if (!subData) {
        const { data: pastDueList } = await supabase
          .from("subscriptions")
          .select("*")
          .eq("user_id", userId)
          .eq("status", "past_due")
          .order("created_at", { ascending: false })
          .limit(1);
        subData = pastDueList?.[0] ?? null;
      }

      setSubscription(subData);

      const plans = await fetchPlansForUser(userId, { orderBy: "display_order" });
      setAvailablePlans(plans);

      if (subData) {
        const plan = plans.find((p) => p.code === subData.plan_code);
        setCurrentPlan(plan || null);
      } else {
        // Se não encontrou assinatura ativa, verificar o current_plan_code do perfil
        const { data: profileData } = await supabase
          .from("profiles")
          .select("current_plan_code")
          .eq("id", userId)
          .maybeSingle();

        const planCode = profileData?.current_plan_code || "free";
        const plan = plans.find((p) => p.code === planCode);
        setCurrentPlan(plan || plans.find((p) => p.code === "free") || null);
      }
    } catch (error) {
      console.error("Error loading subscription:", error);
      toast.error("Erro ao carregar assinatura");
    } finally {
      setLoadingSubscription(false);
    }
  }

  const getStatusBadge = (status: string) => {
    const statusMap = {
      active: { label: "Ativa", variant: "default" as const },
      canceled: { label: "Cancelada", variant: "destructive" as const },
      past_due: { label: "Pendente", variant: "destructive" as const },
    };
    const statusInfo = statusMap[status as keyof typeof statusMap] || { label: status, variant: "secondary" as const };
    return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  const handleSubscribe = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    setShowSubscribeModal(true);
  };

  const handleChangePlan = async (newPlan: SubscriptionPlan) => {
    if (!subscription) return;

    try {
      setLoadingSubscription(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão não encontrada");
        return;
      }

      const { data, error } = await supabase.functions.invoke(
        'pagarme-upgrade-subscription',
        {
          body: { new_plan_code: newPlan.code },
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao alterar plano');

      if (data.payment_method === 'pix' && data.pix_qr_code) {
        setUpgradePixData({
          pix_qr_code: data.pix_qr_code,
          pix_qr_code_url: data.pix_qr_code_url ?? null,
          amount_cents: data.amount_cents ?? data.new_price_cents ?? 0,
          plan_name: data.plan_name ?? newPlan.name,
        });
        toast.success('PIX gerado! Pague para confirmar a alteração do plano.');
      } else {
        toast.success(`Plano alterado para ${newPlan.name}!`);
        const { data: { user } } = await supabase.auth.getUser();
        if (user) await loadSubscriptionData(user.id);
      }
    } catch (err: unknown) {
      console.error('Error changing plan:', err);
      toast.error(err instanceof Error ? err.message : 'Erro ao trocar de plano');
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription) return;

    setCanceling(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão não encontrada");
        return;
      }

      const { error } = await supabase.functions.invoke('pagarme-cancel-subscription', {
        body: {
          subscription_id: subscription.id,
          cancellation_reason: 'Cancelado pelo usuário',
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      toast.success("Assinatura cancelada");
      setShowCancelDialog(false);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) await loadSubscriptionData(user.id);
      useSubscriptionStore.getState().loadSubscription();
    } catch (error) {
      console.error("Error canceling subscription:", error);
      toast.error("Erro ao cancelar");
    } finally {
      setCanceling(false);
    }
  };

  const handleSwitchToPix = async () => {
    if (!subscription) return;
    setSwitchingPaymentMethod(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão não encontrada");
        return;
      }
      const { error } = await supabase.functions.invoke("switch-subscription-payment-method", {
        body: { payment_method: "pix" },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      toast.success("Forma de pagamento alterada para PIX");
      const { data: { user } } = await supabase.auth.getUser();
      if (user) await loadSubscriptionData(user.id);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Erro ao alterar";
      toast.error(msg);
    } finally {
      setSwitchingPaymentMethod(false);
    }
  };

  const handleSwitchToCard = async () => {
    if (!subscription || !selectedCardForSwitch) return;
    setSwitchingPaymentMethod(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Sessão não encontrada");
        return;
      }
      const { error } = await supabase.functions.invoke("switch-subscription-payment-method", {
        body: { payment_method: "credit_card", card_id: selectedCardForSwitch },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      toast.success("Forma de pagamento alterada para cartão de crédito");
      setShowSwitchToCardDialog(false);
      setSelectedCardForSwitch(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) await loadSubscriptionData(user.id);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : "Erro ao alterar";
      toast.error(msg);
    } finally {
      setSwitchingPaymentMethod(false);
    }
  };

  // ========== CARDS FUNCTIONS ==========

  async function loadCards() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase.functions.invoke('pagarme-list-cards', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      setCards(data.cards || []);
    } catch (error: any) {
      console.error("Error loading cards:", error);
      toast.error("Erro ao carregar cartões");
    } finally {
      setLoadingCards(false);
    }
  }

  async function handleAddCard(e: React.FormEvent) {
    e.preventDefault();

    if (!validateCardNumber(cardData.number)) {
      toast.error("Número de cartão inválido");
      return;
    }

    const expMonth = parseInt(cardData.exp_month);
    const expYear = parseInt(cardData.exp_year);

    if (expMonth < 1 || expMonth > 12) {
      toast.error("Mês inválido");
      return;
    }

    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
      toast.error("Cartão vencido");
      return;
    }

    setProcessing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      const { data, error } = await supabase.functions.invoke('pagarme-create-card-direct', {
        body: {
          card_number: cardData.number.replace(/\s/g, ''),
          card_holder_name: cardData.holder_name,
          card_exp_month: cardData.exp_month,
          card_exp_year: cardData.exp_year,
          card_cvv: cardData.cvv,
          set_as_default: cards.length === 0,
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      toast.success('Cartão adicionado!');
      setShowAddCardModal(false);
      setCardData({
        number: "",
        holder_name: "",
        exp_month: "",
        exp_year: "",
        cvv: "",
      });
      await loadCards();

    } catch (error: any) {
      console.error('Error adding card:', error);
      toast.error(error.message || 'Erro ao adicionar cartão');
    } finally {
      setProcessing(false);
    }
  }

  async function handleSetDefault(card: PaymentCard) {
    if (card.is_default) return;

    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      const { error } = await supabase
        .from('payment_cards')
        .update({ is_default: true })
        .eq('id', card.id);

      if (error) throw error;

      await supabase
        .from('payment_cards')
        .update({ is_default: false })
        .neq('id', card.id);

      toast.success('Cartão padrão atualizado');
      await loadCards();

    } catch (error: any) {
      console.error('Error setting default:', error);
      toast.error('Erro ao definir padrão');
    } finally {
      setProcessing(false);
    }
  }

  async function handleDeleteCard() {
    if (!selectedCard) return;

    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      const { error } = await supabase.functions.invoke('pagarme-delete-card', {
        body: { card_id: selectedCard.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      toast.success('Cartão removido');
      setShowDeleteCardDialog(false);
      setSelectedCard(null);
      await loadCards();

    } catch (error: any) {
      console.error('Error deleting card:', error);
      toast.error('Erro ao remover');
    } finally {
      setProcessing(false);
    }
  }

  // ========== INVOICES FUNCTIONS ==========

  async function loadInvoices() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('invoices')
        .select('id, amount_cents, status, paid_at, created_at, pagarme_charge_id, payment_method, nfse_url, nfse_pdf_url, tiny_nfse_id, nfse_number, pix_qr_code_url, pix_expires_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInvoices((data as any) || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Erro ao carregar pagamentos');
    } finally {
      setLoadingInvoices(false);
    }
  }

  async function handleSyncTinyCustomer() {
    setSyncingTinyCustomer(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      toast.loading('Sincronizando cliente com Tiny ERP...', { id: 'sync-customer' });

      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: { action: 'sync-customer', userId: session.user.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      if (!data?.success) {
        const errorMessage = data?.error || 'Erro desconhecido';
        console.error('[Tiny Sync] Error:', errorMessage);
        toast.error('Erro ao sincronizar cliente', {
          id: 'sync-customer',
          description: errorMessage,
          duration: 10000,
        });
        return;
      }

      console.log('[Tiny Sync] Success:', data);

      toast.success('✓ Cliente sincronizado com sucesso!', {
        id: 'sync-customer',
        description: `ID do cliente: ${data.tiny_customer_id}`,
        duration: 5000,
      });

      // Recarregar perfil para pegar o tiny_customer_id
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (profileData) {
          setProfile(profileData);
        }
      }

    } catch (error: any) {
      console.error('[Tiny Sync] Exception:', error);
      toast.error('Erro ao sincronizar cliente', {
        id: 'sync-customer',
        description: error.message,
      });
    } finally {
      setSyncingTinyCustomer(false);
    }
  }

  async function handleDownloadNfsePdf(invoiceId: string) {
    setDownloadingPdf(invoiceId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      toast.loading('Baixando PDF da NFS-e...', { id: 'download-nfse' });

      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: { action: 'download-nfse-pdf', invoiceId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      if (!data?.success) {
        const errorMessage = data?.error || 'Erro desconhecido';
        console.error('[Download NFS-e] Error:', errorMessage);
        toast.error('Erro ao baixar PDF da NFS-e', {
          id: 'download-nfse',
          description: errorMessage,
          duration: 10000,
        });
        return;
      }

      console.log('[Download NFS-e] Success:', data);

      toast.success('✓ PDF da NFS-e baixado com sucesso!', {
        id: 'download-nfse',
        duration: 5000,
      });

      // Recarregar invoices
      await loadInvoices();

      // Abrir PDF em nova aba
      if (data?.data?.nfse_pdf_url) {
        window.open(data.data.nfse_pdf_url, '_blank');
      }

    } catch (error: any) {
      console.error('[Download NFS-e] Exception:', error);
      toast.error('Erro ao baixar PDF da NFS-e', {
        id: 'download-nfse',
        description: error.message,
      });
    } finally {
      setDownloadingPdf(null);
    }
  }

  async function handleGenerateNFSe(invoiceId: string) {
    setGeneratingNfse(invoiceId);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      toast.loading('Gerando NFS-e (sincronizando cliente se necessário)...', { id: 'nfse-generation' });

      const { data, error } = await supabase.functions.invoke('tinyerp', {
        body: {
          action: 'generate-nfse',
          invoiceId,
          userId: session.user.id
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;

      if (!data?.success) {
        const errorMessage = data?.error || 'Erro desconhecido';
        console.error('[NFS-e] Error:', errorMessage);
        toast.error('Erro ao gerar NFS-e', {
          id: 'nfse-generation',
          description: errorMessage,
          duration: 10000,
        });
        return;
      }

      toast.success('✓ NFS-e gerada com sucesso!', {
        id: 'nfse-generation',
        description: `Número: ${data.nfse_number}`
      });

      // Recarregar invoices para atualizar UI
      await loadInvoices();

    } catch (error: any) {
      console.error('[NFS-e] Exception:', error);
      toast.error(error.message || 'Erro ao gerar NFS-e', { id: 'nfse-generation' });
    } finally {
      setGeneratingNfse(null);
    }
  }

  function handleViewInvoice(invoice: Invoice) {
    setSelectedInvoice(invoice);
    setPixDialogData(null);
    setShowInvoiceDialog(true);
  }

  async function handleLoadPixForInvoice(invoice: Invoice) {
    if (invoice.status !== 'pending' || invoice.payment_method !== 'pix') return;

    setLoadingPix(invoice.id);
    setPixDialogData(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão não encontrada');

      const { data, error } = await supabase.functions.invoke('get-or-regenerate-pix', {
        body: { invoice_id: invoice.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Erro ao carregar PIX');

      setPixDialogData({
        pix_qr_code: data.pix_qr_code,
        pix_qr_code_url: data.pix_qr_code_url,
        pix_expires_at: data.pix_expires_at ?? null,
        amount_cents: data.amount_cents ?? invoice.amount_cents,
        regenerated: data.regenerated,
      });

      if (data.regenerated) {
        toast.success('Novo QR code gerado. Email reenviado.');
        await loadInvoices();
      }
    } catch (error: any) {
      console.error('Error loading PIX:', error);
      toast.error(error.message || 'Erro ao carregar PIX');
    } finally {
      setLoadingPix(null);
    }
  }

  function getInvoiceStatusBadge(status: string) {
    const statusMap = {
      paid: { label: 'Pago', variant: 'default' as const, icon: CheckCircle },
      pending: { label: 'Pendente', variant: 'secondary' as const, icon: Clock },
      failed: { label: 'Falha', variant: 'destructive' as const, icon: XCircle },
      refunded: { label: 'Reembolsado', variant: 'outline' as const, icon: RefreshCw },
    };

    const info = statusMap[status as keyof typeof statusMap] || {
      label: status,
      variant: 'secondary' as const,
      icon: AlertCircle
    };

    const Icon = info.icon;

    return (
      <Badge variant={info.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {info.label}
      </Badge>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6 pb-20 md:pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Minha Conta</h1>
          <p className="text-muted-foreground text-sm md:text-base mt-1">Gerencie suas informações e configurações</p>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 pb-20 md:pb-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Minha Conta</h1>
        <p className="text-muted-foreground text-sm md:text-base mt-1">Gerencie suas informações e configurações</p>
      </div>

      <Tabs value={currentTab} onValueChange={handleTabChange} className="space-y-4">
        {/* Mobile: Badges horizontais com scroll */}
        <div className="lg:hidden">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6">
            <Badge
              variant={currentTab === "perfil" ? "default" : "outline"}
              className={cn(
                "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                currentTab === "perfil"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent"
              )}
              onClick={() => handleTabChange("perfil")}
            >
              <User className="h-3.5 w-3.5" />
              <span className="font-medium">Perfil</span>
            </Badge>
            <Badge
              variant={currentTab === "assinatura" ? "default" : "outline"}
              className={cn(
                "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                currentTab === "assinatura"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent"
              )}
              onClick={() => handleTabChange("assinatura")}
            >
              <CreditCard className="h-3.5 w-3.5" />
              <span className="font-medium">Assinatura</span>
            </Badge>
            <Badge
              variant={currentTab === "cartoes" ? "default" : "outline"}
              className={cn(
                "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                currentTab === "cartoes"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent"
              )}
              onClick={() => handleTabChange("cartoes")}
            >
              <Wallet className="h-3.5 w-3.5" />
              <span className="font-medium">Cartões</span>
            </Badge>
            <Badge
              variant={currentTab === "pagamentos" ? "default" : "outline"}
              className={cn(
                "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                currentTab === "pagamentos"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent"
              )}
              onClick={() => handleTabChange("pagamentos")}
            >
              <FileText className="h-3.5 w-3.5" />
              <span className="font-medium">Pagamentos</span>
            </Badge>
            <Badge
              variant={currentTab === "tags" ? "default" : "outline"}
              className={cn(
                "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                currentTab === "tags"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent"
              )}
              onClick={() => handleTabChange("tags")}
            >
              <Tag className="h-3.5 w-3.5" />
              <span className="font-medium">Tags</span>
            </Badge>
            <Badge
              variant={currentTab === "usuarios-conectados" ? "default" : "outline"}
              className={cn(
                "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                currentTab === "usuarios-conectados"
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "hover:bg-accent"
              )}
              onClick={() => handleTabChange("usuarios-conectados")}
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span className="font-medium">Usuários</span>
            </Badge>
          </div>
        </div>

        {/* Desktop: Badges horizontais */}
        <div className="hidden lg:flex gap-2 justify-center flex-wrap">
          <Badge
            variant={currentTab === "perfil" ? "default" : "outline"}
            className={cn(
              "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
              currentTab === "perfil"
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "hover:bg-accent"
            )}
            onClick={() => handleTabChange("perfil")}
          >
            <User className="h-3.5 w-3.5" />
            <span className="font-medium">Perfil</span>
          </Badge>
          <Badge
            variant={currentTab === "assinatura" ? "default" : "outline"}
            className={cn(
              "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
              currentTab === "assinatura"
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "hover:bg-accent"
            )}
            onClick={() => handleTabChange("assinatura")}
          >
            <CreditCard className="h-3.5 w-3.5" />
            <span className="font-medium">Assinatura</span>
          </Badge>
          <Badge
            variant={currentTab === "cartoes" ? "default" : "outline"}
            className={cn(
              "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
              currentTab === "cartoes"
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "hover:bg-accent"
            )}
            onClick={() => handleTabChange("cartoes")}
          >
            <Wallet className="h-3.5 w-3.5" />
            <span className="font-medium">Cartões</span>
          </Badge>
          <Badge
            variant={currentTab === "pagamentos" ? "default" : "outline"}
            className={cn(
              "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
              currentTab === "pagamentos"
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "hover:bg-accent"
            )}
            onClick={() => handleTabChange("pagamentos")}
          >
            <FileText className="h-3.5 w-3.5" />
            <span className="font-medium">Pagamentos</span>
          </Badge>
          <Badge
            variant={currentTab === "tags" ? "default" : "outline"}
            className={cn(
              "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
              currentTab === "tags"
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "hover:bg-accent"
            )}
            onClick={() => handleTabChange("tags")}
          >
            <Tag className="h-3.5 w-3.5" />
            <span className="font-medium">Tags</span>
          </Badge>
          <Badge
            variant={currentTab === "usuarios-conectados" ? "default" : "outline"}
            className={cn(
              "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
              currentTab === "usuarios-conectados"
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "hover:bg-accent"
            )}
            onClick={() => handleTabChange("usuarios-conectados")}
          >
            <UserPlus className="h-3.5 w-3.5" />
            <span className="font-medium">Usuários</span>
          </Badge>
        </div>

        {/* TAB: PERFIL */}
        <TabsContent value="perfil" className="space-y-4 md:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Foto de Perfil</CardTitle>
              <CardDescription className="text-sm">Personalize sua conta</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <Avatar className="h-20 w-20 md:h-24 md:w-24">
                  {avatarUrl ? (
                    <AvatarImage src={avatarUrl} alt="Avatar" />
                  ) : (
                    <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-white text-xl md:text-2xl">
                      {profile.full_name?.substring(0, 2).toUpperCase() || "MS"}
                    </AvatarFallback>
                  )}
                </Avatar>

                <div className="flex flex-col gap-2 w-full sm:w-auto">
                  <Label htmlFor="avatar" className="cursor-pointer">
                    <Button variant="outline" disabled={uploading} asChild className="w-full sm:w-auto">
                      <span>
                        {uploading ? "Enviando..." : avatarUrl ? "Alterar foto" : "Enviar foto"}
                      </span>
                    </Button>
                  </Label>
                  <input
                    id="avatar"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploading}
                  />
                  {avatarUrl && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveAvatar}
                      disabled={uploading}
                      className="w-full sm:w-auto"
                    >
                      Remover foto
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Dados Pessoais</CardTitle>
              <CardDescription className="text-sm">Informações básicas do seu perfil</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nome completo</Label>
                  <Input
                    id="full_name"
                    value={profile.full_name || ""}
                    onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cpf">CPF</Label>
                  <Input
                    id="cpf"
                    value={formatCPF(profile.cpf || "")}
                    onChange={(e) => setProfile({ ...profile, cpf: e.target.value.replace(/\D/g, '') })}
                    maxLength={14}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="health_area">Área da Saúde</Label>
                  <Select
                    value={profile.health_area_id || ""}
                    onValueChange={(value) => setProfile({ ...profile, health_area_id: value })}
                  >
                    <SelectTrigger id="health_area">
                      <SelectValue placeholder="Selecione sua área" />
                    </SelectTrigger>
                    <SelectContent>
                      {healthAreas.map((area) => (
                        <SelectItem key={area.id} value={area.id}>
                          {area.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input
                    id="phone"
                    value={formatPhone(profile.phone || "")}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value.replace(/\D/g, '') })}
                    placeholder="(00) 00000-0000"
                    maxLength={15}
                  />
                </div>
              </div>

              <div className="pt-2">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Endereço
                </h4>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="zipcode">CEP</Label>
                    <Input
                      id="zipcode"
                      value={profile.address_zipcode || ""}
                      onChange={(e) => handleCEPChange(e.target.value)}
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="street">Rua</Label>
                    <Input
                      id="street"
                      value={profile.address_street || ""}
                      onChange={(e) => setProfile({ ...profile, address_street: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="number">Número</Label>
                    <Input
                      id="number"
                      value={profile.address_number || ""}
                      onChange={(e) => setProfile({ ...profile, address_number: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="complement">Complemento</Label>
                    <Input
                      id="complement"
                      value={profile.address_complement || ""}
                      onChange={(e) => setProfile({ ...profile, address_complement: e.target.value })}
                      placeholder="Opcional"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="neighborhood">Bairro</Label>
                    <Input
                      id="neighborhood"
                      value={profile.address_neighborhood || ""}
                      onChange={(e) => setProfile({ ...profile, address_neighborhood: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      value={profile.address_city || ""}
                      onChange={(e) => setProfile({ ...profile, address_city: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state">Estado</Label>
                    <Select
                      value={profile.address_state || ""}
                      onValueChange={(value) => setProfile({ ...profile, address_state: value })}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder="Selecione o estado" />
                      </SelectTrigger>
                      <SelectContent>
                        {BRAZILIAN_STATES.map((state) => (
                          <SelectItem key={state.value} value={state.value}>
                            {state.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Button onClick={handleSaveProfile} disabled={saving} className="w-full md:w-auto">
                {saving ? "Salvando..." : "Salvar alterações"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: ASSINATURA */}
        <TabsContent value="assinatura" className="space-y-4 md:space-y-6">
          {loadingSubscription ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg md:text-xl flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Plano Atual
                  </CardTitle>
                  <CardDescription className="text-sm">Sua assinatura ativa</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {currentPlan && (
                    <>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                          <h3 className="text-xl md:text-2xl font-bold">{currentPlan.name}</h3>
                          <p className="text-muted-foreground text-sm">{currentPlan.description}</p>
                        </div>
                        {subscription && getStatusBadge(subscription.status)}
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between py-2 border-b text-sm">
                          <span className="text-muted-foreground">Valor mensal</span>
                          <span className="font-semibold">{formatPrice(currentPlan.price_cents)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b text-sm">
                          <span className="text-muted-foreground">Pacientes</span>
                          <span className="font-semibold">
                            {currentPlan.min_patients} - {currentPlan.max_patients || "ilimitado"}
                          </span>
                        </div>
                        {subscription?.next_billing_date && (
                          <div className="flex justify-between py-2 border-b text-sm">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              Próxima cobrança
                            </span>
                            <span className="font-semibold">
                              {format(new Date(subscription.next_billing_date), "dd/MM/yyyy", { locale: ptBR })}
                            </span>
                          </div>
                        )}
                        <div className="py-2 border-b">
                          <div className="flex justify-between items-center text-sm mb-2">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <Wallet className="h-4 w-4" />
                              Forma de pagamento
                            </span>
                            <span className="font-semibold">
                              {subscription?.payment_method === "pix"
                                ? "PIX"
                                : subscription?.card_id
                                  ? (() => {
                                      const card = cards.find((c) => c.id === subscription.card_id);
                                      return card
                                        ? `${card.brand.toUpperCase()} •••• ${card.last_four_digits}`
                                        : "Cartão de crédito";
                                    })()
                                  : "Cartão de crédito"}
                            </span>
                          </div>
                          {subscription && (subscription.status === "active" || subscription.status === "past_due") && currentPlan?.code !== "free" && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {subscription?.payment_method === "pix" ? (
                                cards.length === 0 ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleTabChange("cartoes")}
                                  >
                                    <CreditCard className="h-4 w-4 mr-2" />
                                    Adicione um cartão para cobrança automática
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedCardForSwitch(cards.find((c) => c.is_default)?.id ?? cards[0]?.id ?? null);
                                      setShowSwitchToCardDialog(true);
                                    }}
                                    disabled={switchingPaymentMethod}
                                  >
                                    {switchingPaymentMethod ? (
                                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    ) : (
                                      <CreditCard className="h-4 w-4 mr-2" />
                                    )}
                                    Cobrar no cartão
                                  </Button>
                                )
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleSwitchToPix}
                                  disabled={switchingPaymentMethod}
                                >
                                  {switchingPaymentMethod ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <QrCode className="h-4 w-4 mr-2" />
                                  )}
                                  Cobrar via PIX
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {currentPlan.features && currentPlan.features.length > 0 && (
                        <div>
                          <h4 className="font-semibold mb-2 text-sm">Funcionalidades:</h4>
                          <ul className="space-y-2">
                            {currentPlan.features.map((feature: string, index: number) => (
                              <li key={index} className="flex items-start gap-2 text-sm">
                                <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {subscription?.status === 'active' && (
                        <Button
                          variant="destructive"
                          onClick={() => setShowCancelDialog(true)}
                          className="w-full mt-4"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Cancelar Assinatura
                        </Button>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <div>
                <h2 className="text-lg md:text-xl font-bold mb-4">Planos Disponíveis</h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {availablePlans.map((plan) => {
                    const isCurrent = currentPlan?.code === plan.code;
                    const isFreePlan = plan.code === "free";
                    return (
                      <Card key={plan.code} className={isCurrent ? "border-primary" : ""}>
                        <CardHeader>
                          <CardTitle className="flex items-center justify-between text-base">
                            {plan.name}
                            {isCurrent && <Badge>Atual</Badge>}
                          </CardTitle>
                          <CardDescription className="text-sm">{plan.description}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div>
                            <div className="text-2xl font-bold">{formatPrice(plan.price_cents)}</div>
                            <div className="text-xs text-muted-foreground">por mês</div>
                          </div>

                          <div className="text-xs">
                            <strong>Pacientes:</strong> {plan.min_patients} - {plan.max_patients || "ilimitado"}
                          </div>

                          {plan.features && plan.features.length > 0 && (
                            <ul className="space-y-1">
                              {plan.features.map((feature: string, index: number) => (
                                <li key={index} className="flex items-start gap-2 text-xs">
                                  <CheckCircle className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}

                          {!isCurrent && !isFreePlan && subscription?.status !== 'active' && (
                            <Button
                              className="w-full"
                              onClick={() => handleSubscribe(plan)}
                              disabled={loadingSubscription}
                              size="sm"
                            >
                              Assinar
                            </Button>
                          )}

                          {!isCurrent && !isFreePlan && subscription?.status === 'active' && (
                            <Button
                              variant="outline"
                              className="w-full"
                              onClick={() => handleChangePlan(plan)}
                              disabled={loadingSubscription}
                              size="sm"
                            >
                              Trocar
                            </Button>
                          )}

                          {!isCurrent && isFreePlan && subscription?.status === 'active' && (
                            <Button variant="outline" className="w-full" disabled size="sm">
                              Cancelar para voltar
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* TAB: CARTÕES */}
        <TabsContent value="cartoes" className="space-y-4 md:space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg md:text-xl font-bold">Cartões Salvos</h2>
              <p className="text-muted-foreground text-sm">Gerencie seus métodos de pagamento</p>
            </div>
            <Button onClick={() => setShowAddCardModal(true)} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </Button>
          </div>

          {loadingCards ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Skeleton className="h-48" />
              <Skeleton className="h-48" />
            </div>
          ) : cards.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center text-sm mb-4">
                  Nenhum cartão cadastrado
                </p>
                <Button onClick={() => setShowAddCardModal(true)} size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar cartão
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((card) => (
                <Card key={card.id} className={card.is_default ? "border-primary" : ""}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        {card.brand.toUpperCase()}
                      </div>
                      {card.is_default && (
                        <Badge variant="default" className="gap-1">
                          <Star className="h-3 w-3" />
                          Padrão
                        </Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs">{card.holder_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-lg font-mono">
                      •••• •••• •••• {card.last_four_digits}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Validade: {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
                    </div>
                    <div className="flex gap-2">
                      {!card.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => handleSetDefault(card)}
                          disabled={processing}
                        >
                          Padrão
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        className={`${card.is_default ? "w-full" : "flex-1"} text-xs`}
                        onClick={() => {
                          setSelectedCard(card);
                          setShowDeleteCardDialog(true);
                        }}
                        disabled={processing}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TAB: PAGAMENTOS */}
        <TabsContent value="pagamentos" className="space-y-4 md:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg md:text-xl flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Histórico de Pagamentos
              </CardTitle>
              <CardDescription className="text-sm">
                Consulte seus pagamentos de assinatura
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingInvoices ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>Nenhum pagamento realizado ainda</p>
                </div>
              ) : (
                <>
                  {/* Mobile: Cards empilhados */}
                  <div className="md:hidden space-y-3">
                    {invoices.map(invoice => (
                      <Card key={invoice.id} className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          {getInvoiceStatusBadge(invoice.status)}
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(invoice.paid_at || invoice.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                          </span>
                        </div>
                        <div className="text-lg font-semibold mb-3">
                          {formatPrice(invoice.amount_cents)}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Ver detalhes
                          </Button>

                          {/* Ver PIX - pendente PIX */}
                          {invoice.status === 'pending' && invoice.payment_method === 'pix' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                handleViewInvoice(invoice);
                                handleLoadPixForInvoice(invoice);
                              }}
                              disabled={loadingPix === invoice.id}
                            >
                              {loadingPix === invoice.id ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <Wallet className="w-4 h-4 mr-1" />
                              )}
                              Ver PIX
                            </Button>
                          )}

                          {/* Botões NFS-e - Mobile */}
                          {invoice.status === 'paid' && invoice.tiny_nfse_id && (
                            <>
                              {invoice.nfse_pdf_url ? (
                                // PDF disponível - download
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  asChild
                                >
                                  <a href={invoice.nfse_pdf_url} download target="_blank" rel="noopener noreferrer">
                                    <Download className="w-4 h-4 mr-1" />
                                    NFS-e
                                  </a>
                                </Button>
                              ) : invoice.nfse_url ? (
                                // Link da prefeitura - visualizar
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  asChild
                                >
                                  <a href={invoice.nfse_url} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    Ver NFS-e
                                  </a>
                                </Button>
                              ) : (
                                // Tentar baixar
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1"
                                  onClick={() => handleDownloadNfsePdf(invoice.id)}
                                  disabled={downloadingPdf === invoice.id}
                                >
                                  {downloadingPdf === invoice.id ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4 mr-1" />
                                  )}
                                  Baixar NFS-e
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>

                  {/* Desktop: Tabela */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Data</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Método</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoices.map(invoice => (
                          <TableRow key={invoice.id}>
                            <TableCell>{getInvoiceStatusBadge(invoice.status)}</TableCell>
                            <TableCell>
                              {format(new Date(invoice.paid_at || invoice.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                            </TableCell>
                            <TableCell className="font-medium">
                              {formatPrice(invoice.amount_cents)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {invoice.payment_method || 'Cartão de crédito'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-2 justify-end">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleViewInvoice(invoice)}
                                  title="Ver detalhes"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>

                                {/* Ver PIX - pendente PIX */}
                                {invoice.status === 'pending' && invoice.payment_method === 'pix' && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      handleViewInvoice(invoice);
                                      handleLoadPixForInvoice(invoice);
                                    }}
                                    disabled={loadingPix === invoice.id}
                                    title="Ver PIX"
                                  >
                                    {loadingPix === invoice.id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <Wallet className="w-4 h-4" />
                                    )}
                                  </Button>
                                )}

                                {/* Botões NFS-e */}
                                {invoice.status === 'paid' && invoice.tiny_nfse_id && (
                                  <>
                                    {invoice.nfse_pdf_url ? (
                                      // PDF já disponível - download direto
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        asChild
                                        title={`Baixar NFS-e ${invoice.nfse_number || ''}`}
                                      >
                                        <a href={invoice.nfse_pdf_url} download target="_blank" rel="noopener noreferrer">
                                          <Download className="w-4 h-4" />
                                        </a>
                                      </Button>
                                    ) : invoice.nfse_url ? (
                                      // Link da prefeitura disponível - abrir no portal
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        asChild
                                        title="Ver NFS-e no site da prefeitura"
                                      >
                                        <a href={invoice.nfse_url} target="_blank" rel="noopener noreferrer">
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      </Button>
                                    ) : (
                                      // Nem PDF nem link - tentar baixar
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => handleDownloadNfsePdf(invoice.id)}
                                        disabled={downloadingPdf === invoice.id}
                                        title="Baixar PDF da NFS-e"
                                      >
                                        {downloadingPdf === invoice.id ? (
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                          <Download className="w-4 h-4" />
                                        )}
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: TAGS */}
        <TabsContent value="tags">
          <TagManager healthArea={profile.health_area || 'Medicina'} />
        </TabsContent>

        {/* TAB: USUÁRIOS CONECTADOS */}
        <TabsContent value="usuarios-conectados">
          <ConnectedUsersManager />
        </TabsContent>
      </Tabs>

      {/* MODALS */}
      {selectedPlan && (
        <SubscriptionModal
          open={showSubscribeModal}
          onOpenChange={setShowSubscribeModal}
          planCode={selectedPlan.code}
          planName={selectedPlan.name}
          planPrice={selectedPlan.price_cents}
          onSuccess={() => {
            const userId = profile.id;
            if (userId) loadSubscriptionData(userId);
          }}
        />
      )}

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Assinatura</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza? Você perderá acesso aos benefícios no final do período atual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Não, manter</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={canceling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {canceling ? "Cancelando..." : "Sim, cancelar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={showSwitchToCardDialog}
        onOpenChange={(open) => {
          setShowSwitchToCardDialog(open);
          if (!open) setSelectedCardForSwitch(null);
        }}
      >
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Trocar para Cartão de Crédito
            </DialogTitle>
            <DialogDescription className="text-sm">
              Selecione o cartão que será usado nas próximas cobranças
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Cartão</Label>
              <Select
                value={selectedCardForSwitch ?? ""}
                onValueChange={setSelectedCardForSwitch}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um cartão" />
                </SelectTrigger>
                <SelectContent>
                  {cards.map((card) => (
                    <SelectItem key={card.id} value={card.id}>
                      {card.brand.toUpperCase()} •••• {card.last_four_digits} - {card.holder_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowSwitchToCardDialog(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSwitchToCard}
                disabled={!selectedCardForSwitch || switchingPaymentMethod}
              >
                {switchingPaymentMethod ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Confirmar"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddCardModal} onOpenChange={setShowAddCardModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Adicionar Cartão
            </DialogTitle>
            <DialogDescription className="text-sm">
              Insira os dados do cartão
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddCard} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="holder_name">Nome no Cartão</Label>
              <Input
                id="holder_name"
                value={cardData.holder_name}
                onChange={(e) => setCardData({ ...cardData, holder_name: e.target.value.toUpperCase() })}
                placeholder="NOME COMPLETO"
                required
                disabled={processing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="number">Número do Cartão</Label>
              <Input
                id="number"
                value={formatCardNumber(cardData.number)}
                onChange={(e) => setCardData({ ...cardData, number: e.target.value.replace(/\D/g, '') })}
                placeholder="0000 0000 0000 0000"
                maxLength={19}
                required
                disabled={processing}
              />
              {cardData.number && (
                <p className="text-xs text-muted-foreground">
                  Bandeira: {getCardBrand(cardData.number)}
                </p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exp_month">Mês</Label>
                <Input
                  id="exp_month"
                  value={cardData.exp_month}
                  onChange={(e) => setCardData({ ...cardData, exp_month: e.target.value.replace(/\D/g, '') })}
                  placeholder="MM"
                  maxLength={2}
                  required
                  disabled={processing}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp_year">Ano</Label>
                <Input
                  id="exp_year"
                  value={cardData.exp_year}
                  onChange={(e) => setCardData({ ...cardData, exp_year: e.target.value.replace(/\D/g, '') })}
                  placeholder="AA"
                  maxLength={2}
                  required
                  disabled={processing}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cvv">CVV</Label>
                <Input
                  id="cvv"
                  type="password"
                  value={cardData.cvv}
                  onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '') })}
                  placeholder="000"
                  maxLength={4}
                  required
                  disabled={processing}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddCardModal(false)}
                className="flex-1"
                disabled={processing}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={processing} className="flex-1">
                {processing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adicionando...
                  </>
                ) : (
                  "Adicionar"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteCardDialog} onOpenChange={setShowDeleteCardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover Cartão</AlertDialogTitle>
            <AlertDialogDescription>
              Remover cartão terminado em {selectedCard?.last_four_digits}?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCard}
              disabled={processing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {processing ? "Removendo..." : "Sim, remover"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invoice Details Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={(open) => {
        setShowInvoiceDialog(open);
        if (!open) setPixDialogData(null);
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalhes do Pagamento</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                {getInvoiceStatusBadge(selectedInvoice.status)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Valor</span>
                <span className="font-semibold text-lg">
                  {formatPrice(selectedInvoice.amount_cents)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Data</span>
                <span>
                  {format(
                    new Date(selectedInvoice.paid_at || selectedInvoice.created_at),
                    "dd 'de' MMMM 'de' yyyy 'às' HH:mm",
                    { locale: ptBR }
                  )}
                </span>
              </div>
              {selectedInvoice.pagarme_charge_id && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">ID da Cobrança</span>
                  <code className="text-xs bg-muted px-2 py-1 rounded">
                    {selectedInvoice.pagarme_charge_id.slice(0, 16)}...
                  </code>
                </div>
              )}

              {/* PIX - pendente */}
              {selectedInvoice.status === 'pending' && selectedInvoice.payment_method === 'pix' && (
                <div className="pt-4 border-t space-y-3">
                  {!pixDialogData ? (
                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => handleLoadPixForInvoice(selectedInvoice)}
                      disabled={loadingPix === selectedInvoice.id}
                    >
                      {loadingPix === selectedInvoice.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Carregando...
                        </>
                      ) : (
                        <>
                          <Wallet className="w-4 h-4 mr-2" />
                          Ver PIX / Regenerar QR Code
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      {pixDialogData.pix_qr_code_url && (
                        <div className="p-4 bg-white rounded-xl border-2 border-primary/20 flex justify-center">
                          <img
                            src={pixDialogData.pix_qr_code_url}
                            alt="QR Code PIX"
                            className="w-48 h-48"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Código PIX (copia e cola)</Label>
                        <div className="flex gap-2">
                          <Input
                            readOnly
                            value={pixDialogData.pix_qr_code}
                            className="font-mono text-xs"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => {
                              navigator.clipboard.writeText(pixDialogData!.pix_qr_code);
                              toast.success('Código copiado!');
                            }}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-muted-foreground"
                        onClick={() => handleLoadPixForInvoice(selectedInvoice)}
                        disabled={loadingPix === selectedInvoice.id}
                      >
                        {loadingPix === selectedInvoice.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Regenerar QR Code
                      </Button>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4 border-t">
                {selectedInvoice.nfse_url ? (
                  <Button className="w-full" asChild>
                    <a href={selectedInvoice.nfse_url} download>
                      <Download className="w-4 h-4 mr-2" />
                      Baixar NFS-e
                    </a>
                  </Button>
                ) : (
                  <div className="text-center text-sm text-muted-foreground py-3">
                    NFS-e ainda não disponível
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade PIX - QR code para alteração de plano */}
      <Dialog
        open={!!upgradePixData}
        onOpenChange={(open) => {
          if (!open) {
            setUpgradePixData(null);
            supabase.auth.getUser().then(({ data: { user } }) => {
              if (user) loadSubscriptionData(user.id);
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Pague via PIX para confirmar upgrade
            </DialogTitle>
            <DialogDescription>
              Escaneie o QR code ou copie o código PIX para concluir a alteração do seu plano para {upgradePixData?.plan_name}.
            </DialogDescription>
          </DialogHeader>
          {upgradePixData && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-2xl font-semibold text-primary mb-2">
                  {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(upgradePixData.amount_cents / 100)}
                </p>
              </div>
              {upgradePixData.pix_qr_code_url && (
                <div className="p-4 bg-white rounded-xl border-2 border-primary/20 flex justify-center">
                  <img
                    src={upgradePixData.pix_qr_code_url}
                    alt="QR Code PIX"
                    className="w-48 h-48"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Código PIX (copia e cola)</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={upgradePixData.pix_qr_code}
                    className="font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(upgradePixData!.pix_qr_code);
                      toast.success("Código copiado!");
                    }}
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Após o pagamento, seu plano será atualizado automaticamente em instantes.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
